import Layout from '@/layouts/_layout';
import { NextPageWithLayout } from '@/types';
import axios from 'axios';
import { GetStaticPaths, GetStaticProps } from 'next';
import { Article } from '../../types/index';
import { Avatar, LinkIcon } from '@nextui-org/react';
import {
  FacebookShareButton,
  FacebookIcon,
  TwitterShareButton,
  TwitterIcon,
  LinkedinShareButton,
  LinkedinIcon,
} from 'react-share';
import routes from '@/config/routes';
import { useState } from 'react';
import { useCopyToClipboard } from 'react-use';
import { useTranslation } from 'react-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { ArticleJsonLd, NextSeo } from 'next-seo';
import Image from '@/components/ui/image';
import Seo from '@/layouts/_seo';
import Breadcrumb from '@/components/navigation/BreadCrumb';

interface ArticleProps {
  article: Article;
}

const ArticlePage: NextPageWithLayout<ArticleProps> = ({ article }) => {
  const { attributes } = article;
  const {
    Title,
    Content,
    publishedAt,
    Thumbnail,
    categories,
    author,
    Slug,
    Summary,
    seo,
  } = attributes;
  const { t } = useTranslation('common');
  const articleUrl = `${process.env.NEXT_PUBLIC_WEBSITE_URL}${routes.articleUrl(
    Slug
  )}`;
  let [copyButtonStatus, setCopyButtonStatus] = useState(t('text-copy-link'));
  let [_, copyToClipboard] = useCopyToClipboard();
  const handleCopyToClipboard = () => {
    copyToClipboard(articleUrl);
    setCopyButtonStatus(t('text-copied'));
    setTimeout(() => {
      setCopyButtonStatus(copyButtonStatus);
    }, 1000);
  };

  const socialMediaData = (network: string) => {
    return seo?.metaSocial?.find((social) => social.socialNetwork === network);
  };

  const twitterData = socialMediaData('Twitter');
  const facebookData = socialMediaData('Facebook');

  return (
    <>
      <Seo
        title={seo?.metaTitle || Title}
        description={seo?.metaDescription || Summary}
        url={`/blog/${Slug}`}
        canonical={seo?.canonicalURL}
        twitter={{
          handle: '@PromptsClub',
          site: '@PromptsClub',
          cardType: 'summary_large_image',
        }}
        openGraph={{
          type: 'article',
          url: articleUrl,
          title: seo?.metaTitle || Title,
          description: seo?.metaDescription || Summary,
          images: [
            {
              url: Thumbnail?.data?.attributes?.url || '',
              alt: seo?.metaTitle || Title,
            },
            ...(seo?.metaImage?.data
              ? [
                  {
                    url: seo.metaImage.data.attributes.url,
                    alt: seo.metaImage.data.attributes.alternativeText || Title,
                  },
                ]
              : []),
            ...(facebookData?.image?.data
              ? [
                  {
                    url: facebookData.image.data.attributes.url,
                    alt: facebookData.title || Title,
                  },
                ]
              : []),
          ],
        }}
        additionalMetaTags={[
          ...(seo?.keywords
            ? [{ name: 'keywords', content: seo.keywords }]
            : []),
          ...(seo?.metaRobots
            ? [{ name: 'robots', content: seo.metaRobots }]
            : []),
          ...(seo?.metaViewport
            ? [{ name: 'viewport', content: seo.metaViewport }]
            : []),
        ]}
      />
      <ArticleJsonLd
        useAppDir={false}
        url={articleUrl}
        title={seo?.metaTitle || Title}
        images={[
          ...(seo?.metaImage?.data ? [seo.metaImage.data.attributes.url] : []),
          ...(Thumbnail?.data?.attributes?.url
            ? [Thumbnail.data.attributes.url]
            : []),
        ]}
        datePublished={attributes.publishedAt}
        dateModified={attributes.updatedAt || attributes.publishedAt}
        authorName={[
          {
            name: author?.data?.attributes?.Name || '',
            // TODO: url: `https://example.com/authors/${author?.data?.id || ''}`,
          },
        ]}
        publisherName="Prompts Club"
        publisherLogo="https://promptsclub.com/favicon.ico"
        description={seo?.metaDescription || Summary}
        isAccessibleForFree={true}
        keywords={seo?.keywords}
        structuredData={seo?.structuredData || null}
      />
      <div className="mx-auto max-w-screen-md px-4 py-8 sm:py-16">
        <div className="mb-4 sm:mb-6">
          <Breadcrumb fallbackUrl={routes.blog} />
        </div>
        {/* Category and metadata */}
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-sm sm:text-[15px]">
          {categories.data.map((category) => (
            <span
              key={category.id}
              className="whitespace-nowrap rounded-full border border-black py-1 px-2 text-xs font-medium uppercase tracking-wide text-black dark:border-white dark:text-white"
            >
              {category.attributes.Name}
            </span>
          ))}
          <div className="flex flex-wrap items-center text-xs sm:text-sm mt-2 sm:mt-0 gap-3">
            <span className="whitespace-nowrap">
              {new Date(publishedAt).toLocaleDateString()}
            </span>
            <span className="px-2.5 hidden sm:inline">⋅</span>
            <span className="whitespace-nowrap">
              {Math.ceil(Content.split(' ').length / 200)} min read
            </span>
          </div>
        </div>

        {/* Article Title */}
        <h1 className="mt-4 sm:mt-6 text-2xl sm:text-3xl font-bold leading-tight sm:leading-snug md:text-[2.5rem]">
          {Title}
        </h1>

        {/* Author Information */}
        <div className="mt-4 flex items-center gap-2">
          <Avatar
            showFallback
            src={
              author.data.attributes.Avatar.data.attributes.formats.thumbnail
                ?.url
            }
            size="sm"
          />
          <p className="text-sm font-medium">
            {author.data.attributes.Name}
          </p>
        </div>

        {/* Article Image */}
        <div className="relative mt-8 sm:mt-12 block bg-black/5 dark:bg-white/5">
          <Image
            alt={Title}
            src={Thumbnail.data.attributes.url}
            width={1200}
            height={900}
            layout="responsive"
          />
        </div>

        {/* Article Content */}
        <div className="prose mt-8 sm:mt-16 max-w-none dark:prose-invert sm:prose-lg prose-figcaption:mt-2 prose-figcaption:text-center prose-figcaption:text-sm">
          <div dangerouslySetInnerHTML={{ __html: Content }} />
        </div>

        {/* Social Sharing Links */}
        <div className="mt-8 sm:mt-16 flex flex-wrap items-center gap-4">
          <span className="text-sm font-semibold uppercase">Share:</span>
          <div className="flex flex-wrap gap-2 md:gap-3">
            <div className="product-share flex flex-shrink-0 flex-wrap items-center gap-1.5 md:gap-2.5">
              <FacebookShareButton url={articleUrl}>
                <FacebookIcon
                  round
                  className="h-8 w-8 sm:h-9 sm:w-9 xl:h-10 xl:w-10"
                />
              </FacebookShareButton>
              <TwitterShareButton url={articleUrl}>
                <TwitterIcon
                  round
                  className="h-8 w-8 sm:h-9 sm:w-9 xl:h-10 xl:w-10"
                />
              </TwitterShareButton>
              <LinkedinShareButton url={articleUrl}>
                <LinkedinIcon
                  round
                  className="h-8 w-8 sm:h-9 sm:w-9 xl:h-10 xl:w-10"
                />
              </LinkedinShareButton>
            </div>
            <button
              className="flex h-8 sm:h-9 xl:h-10 flex-shrink-0 items-center rounded-full border border-light-600 px-3 text-dark-600 hover:bg-light-200 hover:text-dark dark:border-dark-500 dark:text-light-600 hover:dark:bg-dark-500 dark:hover:text-light md:px-4"
              onClick={handleCopyToClipboard}
            >
              <LinkIcon className="h-3.5 w-3.5 text-dark-700 ltr:mr-1.5 rtl:ml-1.5 dark:text-light lg:h-4 lg:w-4" />
              <span>{copyButtonStatus}</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

// Layout wrapper
ArticlePage.getLayout = function getLayout(page) {
  return <Layout>{page}</Layout>;
};

export default ArticlePage;

const API_URL = process.env.NEXT_PUBLIC_STRAPI_API_ENDPOINT;
const API_TOKEN = process.env.NEXT_PUBLIC_STRAPI_API_TOKEN;

/**
 * Fetch all article slugs from the backend.
 * @returns {Promise<{ params: { slug: string } }[]>}
 */
export async function fetchAllArticleSlugs() {
  try {
    const response = await axios.get(`${API_URL}/articles`, {
      headers: {
        Authorization: `Bearer ${API_TOKEN}`,
      },
      params: {
        fields: ['slug'], // Make sure to adjust according to your API's requirements
      },
    });

    // Map through the data to extract slugs and format them for getStaticPaths
    const slugs = response.data.data.map((article: Article) => ({
      params: { articleSlug: article.attributes.Slug },
    }));

    return slugs;
  } catch (error) {
    console.error('Failed to fetch article slugs:', error);
    return []; // Return an empty array on error
  }
}

/**
 * Fetches an article by its slug, including related categories, author, and thumbnails.
 *
 * @param slug - The slug of the article to fetch.
 * @returns The data for the article, or throws an error if the fetch fails.
 */
export const fetchArticleBySlug = async (slug: string) => {
  try {
    const response = await axios.get(`${API_URL}/articles`, {
      headers: {
        Authorization: `Bearer ${API_TOKEN}`,
      },
      params: {
        'filters[Slug][$eq]': slug, // Strapi uses this format to filter by specific fields
        populate: 'Thumbnail,categories,author,author.Avatar', // Fetches additional details about categories, author, and thumbnail in one API call
      },
    });

    // Check if the response contains data
    if (response.data && response.data.data) {
      // Find the exact article matching the slug (if multiple are returned)
      const article = response.data.data.find((item: Article) => item.attributes.Slug === slug);

      if (article) {
        return article; // Return the exact article found
      } else {
        console.warn(`No exact match found for slug: ${slug}`);
        return null; // Handle case when no exact match is found
      }
    } else {
      console.warn(`No article data returned for slug: ${slug}`);
      return null; // Handle case when no data is returned
    }
  } catch (error) {
    console.error('Failed to fetch article:', error);
    throw error; // Optionally, you might want to handle this error more gracefully
  }
};

export const getStaticPaths: GetStaticPaths = async () => {
  const paths = await fetchAllArticleSlugs();
  return {
    paths,
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps = async ({ params, locale }) => {
  const { articleSlug } = params as { articleSlug: string }; // Ensure the slug is correctly destructured
  if (typeof articleSlug !== 'string') {
    return { notFound: true };
  }

  const article = await fetchArticleBySlug(articleSlug);

  return {
    props: {
      article,
      ...(await serverSideTranslations(locale!, ['common'])),
    },
    revalidate: 60, // Optional revalidation interval in seconds
  };
};
